<template>
	<div class="results-scroll">
		<ul v-if="matchingLocations.length" id="location-list" class="results-list list-unstyled">
			<LocationCard
				v-for="location in matchingLocations"
				:key="location.mmsClubId"
				:location="location"
				:is-selected="isSelected(location)"
				:is-mobile-map-view="isMobileMapView"
				:data-testid="`location-card-${location.mmsClubId}`"
				@keyup.enter="onKeyupEnter(location)"
				@click="$emit('card-click', location)"
				@mouseenter="$emit('card-hover-change', location)"
				@mouseleave="$emit('card-hover-change', null)"
			/>
		</ul>

		<DigitalOnlyMembership v-else :has-filters="hasFilters" :is-mobile-map-view="isMobileMapView" />
	</div>
</template>

<script setup lang="ts">
import { watch } from 'vue';

import LocationCard from 'Lib/components/find-a-location/LocationCard.vue';
import type { Location } from 'Types/aemui/find-a-location/findaLocation';
import DigitalOnlyMembership from 'Lib/components/find-a-location/DigitalOnlyMembership.vue';

const props = defineProps<{
	hasFilters: boolean;
	matchingLocations: Location[];
	selectedLocation: Location | null;
	isMobileMapView: boolean;
}>();

const emit = defineEmits<{
	(e: 'card-click', location: Location): void;
	(e: 'card-hover-change', location: Location | null): void;
}>();

function isSelected(location: Location): boolean {
	return props.selectedLocation?.mmsClubId === location.mmsClubId;
}

// There is an issue where, when a user attempts to close the infoWindow using
// the ENTER key while focused on the infoWindow's X button. It just opens again
// because:
//   - the infoWindow closes
//   - `info-window-close` is emitted, and the selectedLocation is set to null
//   - focus returns to the location card
//   - keyup ENTER is fired on the location card (NO CLUE WHY or what does this)
//   - `card-click` fires which sets the selectedLocation and opens the
//      infoWindow
// All this code is an attempt to interrupt this flow so a user can close the
// infoWindow using the ENTER key on the infoWindow and not have it open again.

/** the location that used to be selected but no longer is */
let closedLocation: Location | null;
/** the timestamp (in ms) of when the selectedLocation last changed */
let closedTime: number;

watch(
	() => props.selectedLocation,
	(_, oldSelectedLocation) => {
		closedLocation = oldSelectedLocation;
		closedTime = Date.now();
	}
);
const onKeyupEnter = (location: Location) => {
	// If a recently closed location is this location, just ignore this keypress
	// instead of opening it right after it just closed.
	const recentThresholdMs = 250;
	const lastChange = closedTime?.valueOf() || 0;
	const thisLocationWasRecentlyClosed =
		closedLocation?.mmsClubId === location.mmsClubId && Date.now() - lastChange < recentThresholdMs;
	if (thisLocationWasRecentlyClosed) {
		return; // ignore it
	}
	// otherwise, let it go through
	emit('card-click', location);
};
</script>
